import { ref, computed, watch } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { TaskType } from "@/utils/enums";
import { toBadgeCount } from "@/utils/badgeCount";

export function useInteractionTabs(props: any, reactiveContact: any, incomingCallData: any) {
  const activeInteractionTab = ref("calls");
  const interactionTabs = ref([
    { id: "calls", label: "Calls", icon: "solar:phone-bold" },
    { id: "email", label: "Email", icon: "mdi:email-outline" },
    { id: "linkedin", label: "LinkedIn", icon: "mdi:linkedin" },
    { id: "whatsapp", label: "WhatsApp", icon: "mdi:whatsapp" },
    { id: "sms", label: "SMS", icon: "mdi:message-text-outline" },
    { id: "chat", label: "Chat", icon: "mdi:chat-outline" },
  ]);
  const isLoadingConversations = ref(false);
  const queryClient = useQueryClient();

  // Interaction counts query
  const contactIdForCounts = computed(() => reactiveContact.value?.id ?? null);
  const { data: interactionCountsData } = useQuery({
    queryKey: computed(() => ["interactionCounts", contactIdForCounts.value]),
    queryFn: async () => {
      const { default: baseAPI } = await import("@/api/base");
      const response = await baseAPI.get(`/contact/${contactIdForCounts.value}/interaction-counts`);
      return response.data?.data ?? null;
    },
    enabled: computed(() => !!contactIdForCounts.value),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const interactionCounts = computed(() => interactionCountsData.value);

  // Computed properties
  const tabsWithBadges = computed(() => {
    if (!interactionCounts.value) return interactionTabs.value;
    const badgeMap: Record<string, number> = {
      calls: interactionCounts.value.calls?.missed || 0,
      email: interactionCounts.value.email?.unread || 0,
      linkedin: interactionCounts.value.linkedin?.unread || 0,
      whatsapp: interactionCounts.value.whatsapp?.unread || 0,
      sms: interactionCounts.value.sms?.unread || 0,
      // The chat endpoint currently returns a boolean has-unread flag rather
      // than a count like the other channels — normalize so a real count
      // (if the backend ever sends one) still renders, without leaking a
      // literal "true"/"false" into the badge.
      chat: toBadgeCount(interactionCounts.value.chat?.unread),
    };
    return interactionTabs.value.map((tab: any) => ({
      ...tab,
      badge: badgeMap[tab.id] || 0,
    }));
  });

  // Functions
  function incrementBadge(channel: string) {
    if (channel === activeInteractionTab.value) return;
    const contactId = contactIdForCounts.value;
    if (!contactId) return;
    const key = channel === "calls" ? "missed" : "unread";

    queryClient.setQueryData(["interactionCounts", contactId], (old: any) => {
      if (!old?.[channel]) return old;
      return {
        ...old,
        [channel]: {
          ...old[channel],
          [key]: (old[channel][key] || 0) + 1,
        },
      };
    });
  }

  function resetBadge(channel: string) {
    const contactId = contactIdForCounts.value;
    if (!contactId) return;
    const key = channel === "calls" ? "missed" : "unread";

    queryClient.setQueryData(["interactionCounts", contactId], (old: any) => {
      if (!old?.[channel]) return old;
      return {
        ...old,
        [channel]: {
          ...old[channel],
          [key]: 0,
        },
      };
    });
  }

  function mapTaskTypeToTab(taskType: string): string {
    if (!taskType) return "calls";
    if (taskType === "chat") return "chat";
    if (taskType === TaskType.email) return "email";
    if (taskType === TaskType.linkedIn) return "linkedin";
    if (taskType === TaskType.call) return "calls";
    if (taskType === TaskType.sms) return "sms";
    if (taskType === TaskType.whatsApp) return "whatsapp";
    return "calls";
  }

  async function loadConversationsForChannel(channelType: string) {
    if (!channelType || channelType === "calls") return;

    const contactId = reactiveContact.value?.id;
    if (!contactId) return;

    if (channelType === "whatsapp") {
      await queryClient.invalidateQueries({ queryKey: ["whatsapp-conversations", contactId] });
    } else if (channelType === "linkedin") {
      await queryClient.invalidateQueries({ queryKey: ["linkedin-chat-by-contact", contactId] });
    } else if (channelType === "sms") {
      await queryClient.invalidateQueries({ queryKey: ["sms-conversations", contactId] });
    }
  }

  // Watchers
  watch(
    () => props.selectedBucket,
    async (newBucket) => {
      if (!props.incomingCallData) {
        activeInteractionTab.value = mapTaskTypeToTab(newBucket?.taskType);
      }
      await loadConversationsForChannel(activeInteractionTab.value);
    },
    { immediate: true }
  );

  watch(
    () => props.incomingCallData,
    (newData) => {
      if (newData) {
        activeInteractionTab.value = "calls";
      }
    },
    { immediate: true }
  );

  watch(
    activeInteractionTab,
    async (newTab) => {
      resetBadge(newTab);

      if (newTab === "calls" && reactiveContact.value?.id) {
        try {
          const { default: baseAPI } = await import("@/api/base");
          await baseAPI.patch(`/contact/${reactiveContact.value.id}/calls/seen`);
        } catch (error) {
          console.error("Failed to mark calls as seen:", error);
        }
      }

      if ((newTab === "linkedin" || newTab === "whatsapp") && reactiveContact.value?.id) {
        try {
          const { default: Contact } = await import("@/services/contact/contact");
          await Contact.markChannelNotificationsSeen(reactiveContact.value.id, newTab);
          // Buckets table's Activity column is a separate bulk query, keyed by
          // the page's contactIds — invalidate by prefix so it refetches too.
          queryClient.invalidateQueries({ queryKey: ["engagement-summary"] });
        } catch (error) {
          console.error(`Failed to mark ${newTab} notifications as seen:`, error);
        }
      }

      await queryClient.invalidateQueries({
        queryKey: ["interactionCounts", contactIdForCounts.value],
      });

      await loadConversationsForChannel(newTab);
    },
    { immediate: true }
  );

  return {
    activeInteractionTab,
    interactionTabs,
    isLoadingConversations,
    tabsWithBadges,
    interactionCounts,
    incrementBadge,
    resetBadge,
    mapTaskTypeToTab,
    loadConversationsForChannel,
  };
}
